import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface UIState {
  sidebarOpen: boolean;
  theme: 'light' | 'dark';
  themeMode: 'auto' | 'manual'; // Track if theme is in auto or manual mode
  loading: {
    global: boolean;
    [key: string]: boolean;
  };
  notifications: Array<{
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
    timestamp: number;
    read: boolean;
  }>;
  modals: {
    [key: string]: {
      open: boolean;
      data?: any;
    };
  };
  filters: {
    [key: string]: any;
  };
}

const initialState: UIState = {
  sidebarOpen: true,
  theme: (localStorage.getItem('theme') as 'light' | 'dark') || 'light',
  themeMode: (localStorage.getItem('themeMode') as 'auto' | 'manual') || 'auto',
  loading: {
    global: false,
  },
  notifications: [],
  modals: {},
  filters: {},
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;
      state.themeMode = 'manual'; // Switch to manual mode when user explicitly sets theme
      localStorage.setItem('theme', action.payload);
      localStorage.setItem('themeMode', 'manual');
    },
    setThemeMode: (state, action: PayloadAction<'auto' | 'manual'>) => {
      state.themeMode = action.payload;
      localStorage.setItem('themeMode', action.payload);
    },
    updateAutoTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      // Only update theme if in auto mode
      if (state.themeMode === 'auto') {
        state.theme = action.payload;
        localStorage.setItem('theme', action.payload);
      }
    },
    setLoading: (state, action: PayloadAction<{ key?: string; loading: boolean }>) => {
      const { key = 'global', loading } = action.payload;
      state.loading[key] = loading;
    },
    addNotification: (state, action: PayloadAction<{
      type: 'success' | 'error' | 'warning' | 'info';
      title: string;
      message: string;
    }>) => {
      const notification = {
        id: Date.now().toString(),
        ...action.payload,
        timestamp: Date.now(),
        read: false,
      };
      state.notifications.unshift(notification);
      
      // Keep only last 50 notifications
      if (state.notifications.length > 50) {
        state.notifications = state.notifications.slice(0, 50);
      }
    },
    markNotificationAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(n => n.id === action.payload);
      if (notification) {
        notification.read = true;
      }
    },
    markAllNotificationsAsRead: (state) => {
      state.notifications.forEach(notification => {
        notification.read = true;
      });
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    openModal: (state, action: PayloadAction<{ key: string; data?: any }>) => {
      const { key, data } = action.payload;
      state.modals[key] = { open: true, data };
    },
    closeModal: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      if (state.modals[key]) {
        state.modals[key].open = false;
        state.modals[key].data = undefined;
      }
    },
    setFilter: (state, action: PayloadAction<{ key: string; value: any }>) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    clearFilter: (state, action: PayloadAction<string>) => {
      delete state.filters[action.payload];
    },
    clearAllFilters: (state) => {
      state.filters = {};
    },
  },
});

export const {
  toggleSidebar,
  setSidebarOpen,
  setTheme,
  setThemeMode,
  updateAutoTheme,
  setLoading,
  addNotification,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  removeNotification,
  clearNotifications,
  openModal,
  closeModal,
  setFilter,
  clearFilter,
  clearAllFilters,
} = uiSlice.actions;

export default uiSlice.reducer;

// Selectors
export const selectUI = (state: { ui: UIState }) => state.ui;
export const selectSidebarOpen = (state: { ui: UIState }) => state.ui.sidebarOpen;
export const selectTheme = (state: { ui: UIState }) => state.ui.theme;
export const selectThemeMode = (state: { ui: UIState }) => state.ui.themeMode;
export const selectLoading = (state: { ui: UIState }, key = 'global') => state.ui.loading[key] || false;
export const selectNotifications = (state: { ui: UIState }) => state.ui.notifications;
export const selectUnreadNotifications = (state: { ui: UIState }) => 
  state.ui.notifications.filter(n => !n.read);
export const selectModal = (state: { ui: UIState }, key: string) => state.ui.modals[key];
export const selectFilter = (state: { ui: UIState }, key: string) => state.ui.filters[key];